/**
 * Centralt sted for ALLE UI-strenge på sitet.
 * Klar til at blive flyttet til Supabase når kontrolpanel-features bygges.
 *
 * Struktur: ét stort objekt per locale med samme nøgle-struktur.
 * Hver komponent/side får sin egen sub-nøgle.
 *
 * Indhold der hentes fra GonDrive API forbliver på dansk uanset sprog
 * (per memory-fil).
 */

import type { Locale } from "./config";

export type Dictionary = typeof daRaw;

const daRaw = {
  // ─── Generel UI ──────────────────────────────────────────────────
  common: {
    callUs: "Ring til os",
    smsUs: "Send SMS",
    findCourse: "Find dit forløb →",
    seeAllPrices: "Se alle priser",
    seePrices: "Se priser",
    seeMore: "Læs mere",
    contactUs: "Kontakt os",
    enrollClass: "Tilmeld Hold",
    seeClasses: "Se hold",
    nextClass: "Næste holdstart",
    seatsAvailable: "Ledige pladser",
    seatsLabel: "Ledige",
    fullLabel: "Fuldt",
    fullyBooked: "Fuldt booket",
    sendMessage: "Send besked →",
    name: "Navn",
    email: "E-mail",
    phone: "Telefonnummer",
    phoneOptional: "Telefonnummer (valgfri)",
    message: "Hvad har du på hjerte?",
    sending: "Sender...",
    loading: "Indlæser…",
    noUpcoming: "Ingen kommende hold",
    home: "Forside",
    openMenu: "Åbn menu",
    closeMenu: "Luk menu",
    quickContact: "Hurtig kontakt",
    callShort: "Ring",
    next: "Næste",
    previous: "Forrige",
    free: "Fra",
    standardPackageFrom: "Lovpakke fra",
    openingHoursWeekdays: "Man–fre 08–19",
  },

  // ─── Navigation ──────────────────────────────────────────────────
  nav: {
    bil: "Bil",
    motorcykel: "Motorcykel",
    trailer: "Trailer",
    priser: "Priser",
    holdstart: "Holdstart",
    butik: "Butik",
    omOs: "Om os",
    kontakt: "Kontakt",
    artikler: "Artikler",
    forside: "Forside",
    generhvervelse: "Generhvervelse",
    seHold: "Se hold",
    primaryAriaLabel: "Primær",
  },

  // ─── Mega-menu (desktop, "Bil"-menupunkt) ────────────────────────
  megaBil: {
    col1Heading: "Kørekort til bil",
    col1: [
      { title: "Personbil", desc: "Kategori B — lovpakke", href: "/personbil" },
      { title: "Automatgear", desc: "Uden at skifte gear", href: "/personbil" },
      { title: "På engelsk", desc: "English driving school", href: "/en/personbil" },
    ],
    col2Heading: "Generhvervelse",
    col2: [
      { title: "Generhvervelse", desc: "Efter frakendelse", href: "/generhvervelse" },
      { title: "Kontrollerende prøve", desc: "Sådan forbereder du dig", href: "/generhvervelse" },
      { title: "ANT-kursus", desc: "Ved spritfrakendelse", href: "/generhvervelse" },
    ],
    col3Heading: "Andet",
    col3: [
      { title: "Se alle priser", desc: "Pakker og tillæg", href: "/priser" },
      { title: "Næste holdstart", desc: "Ledige pladser", href: "/holdstart" },
      { title: "Find dit forløb", desc: "Guide til valg", href: "/personbil" },
    ],
    promoKicker: "Populær",
    promoBody: "All inclusive-pakken — alt du skal bruge for at bestå første gang.",
    promoCta: "Se pakken →",
    promoHref: "/personbil",
  },

  // ─── Header ──────────────────────────────────────────────────────
  header: {
    homeAriaLabel: "Brøndby Køreskole — forside",
  },

  // ─── Footer ──────────────────────────────────────────────────────
  footer: {
    description:
      "Din lokale køreskole i Brøndby siden 2013. Personbil, motorcykel, trailer og generhvervelse — med fokus på kvalitet og tryghed.",
    coursesHeader: "Forløb",
    schoolHeader: "Køreskolen",
    contactHeader: "Kontakt",
    teachingLocation: "Undervisning",
    companyAddressLabel: "Firmaadresse",
    companyAddress: "Hvidovre Torv 6A, 2650 Hvidovre",
    termsLink: "Handelsbetingelser",
    privacyLink: "Cookie- og privatlivspolitik",
    developedBy: "Udviklet af Firma360",
    copyright: "© {year} Brøndby Køreskole",
    cvr: "CVR {cvr}",
  },

  // ─── Hero (forside) ──────────────────────────────────────────────
  hero: {
    eyebrow: "Din lokale køreskole i Brøndby",
    title: "Din trygge vej til kørekortet —",
    titleEm: "med kvalitet og erfaring",
    titlePunctuation: ".",
    lead: "Seriøs køreundervisning, i et afslappet og motiverende miljø.",
    statYears: "Års erfaring",
    statClients: "Tilfredse elever",
    statInstructors: "Rummelige kørelærere",
    statRating: "på Facebook",
    callToAction: "Ring 52 40 40 20 for information om næste hold",
  },

  // ─── New Owner Strip ─────────────────────────────────────────────
  newOwnerStrip: {
    ariaLabel: "Ny ejer og nyt team",
    tag: "Nyt siden aug. 2025",
    headlineStrong: "Nye ejere! Nyt team! Ny energi!",
    body:
      "Danny Fernandez har overtaget Brøndby Køreskole — samme lokation, frisk tilgang, fortsat høj kvalitet. Som tidligere set på TV2.",
  },

  // ─── Callback CTA ────────────────────────────────────────────────
  callback: {
    eyebrow: "Bliv ringet op",
    title: "Lad os tage",
    titleEm: "en samtale",
    body:
      "Send os en SMS — angiv gerne hvornår det passer dig at blive ringet op, så vender vi tilbage på det ønskede tidspunkt.",
    cta: "Ring til mig →",
    smsBody: "Ring mig venligst op.",
  },

  // ─── Feature Highlights (4 kort) ─────────────────────────────────
  featureHighlights: {
    items: [
      { title: "Kendt fra TV2", desc: "Måske har du allerede set os på skærmen" },
      { title: "Høj beståelsesprocent", desc: "Vi guider dig hele vejen til kørekortet" },
      { title: "Fleksible køretider", desc: "Tilpasset skole, arbejde og fritid" },
      { title: "Pædagogisk teoriundervisning", desc: "Med fokus på det, der virker i praksis" },
    ],
  },

  // ─── Intro Text ──────────────────────────────────────────────────
  introText: {
    eyebrow: "Lokal køreskole siden 2013",
    title: "Brøndby Køreskole —",
    titleEm: "din lokale køreskole",
    titleSuffix: "i Brøndby",
    lead: "Trygt, effektivt og professionelt.",
    body: [
      "Brøndby Køreskole har siden 2013 hjulpet elever i Brøndby og omegn med at tage kørekort.",
      "Vi holder til på Nygårds Plads 2K – lige ved Brøndbyøster Station, så du nemt kan komme til teoriundervisning med både tog og bus.",
    ],
    goalLabel: "Vores mål er klart:",
    goalBody:
      "At give dig en tryg, grundig og moderne vej til kørekortet. Hos os får du ikke bare konkurrencedygtige priser – du får først og fremmest kvalitetsundervisning, hvor vi har fokus på at uddanne sikre og ansvarlige bilister, der kan færdes trygt i trafikken.",
    bullets: [
      { text: "Erfarne og engagerede kørelærere, der støtter dig hele vejen", strong: "Erfarne og engagerede kørelærere" },
      { text: "Et afslappet og motiverende læringsmiljø", strong: "Et afslappet og motiverende" },
      { text: "Teoriundervisning og køretimer, der passer til dit tempo", strong: "Teoriundervisning og køretimer" },
      { text: "Mulighed for både bil, motorcykel, trailerkørekort samt engelsk hold", strong: "bil, motorcykel, trailerkørekort" },
    ],
    closingBody:
      "Uanset om du er førstegangselev eller skal generhverve dit kørekort, står vi klar til at hjælpe dig. Hos Brøndby Køreskole er du sikret seriøs undervisning, høj beståelsesprocent og en god oplevelse hele vejen til kørekortet.",
  },

  // ─── GonDrive Band ───────────────────────────────────────────────
  gondrive: {
    tag: "Drevet af GonDrive",
    title: "Hele forløbet — samlet ét sted",
    body:
      "Vi bruger GonDrive som vores elev- og administrationssystem, så du som elev har booking, teori, modulplan og betaling tilgængeligt samme sted — både på mobil og computer.",
    chips: ["Booking", "Teori", "Modulplan", "Betaling"],
  },

  // ─── Forløb Grid (4 kort på forsiden) ────────────────────────────
  forloeb: {
    eyebrow: "Kørekortpakker i Brøndby",
    title: "Find den",
    titleEm: "kørekortpakke",
    titleSuffix: ", der passer til dig",
    lead:
      "Hos Brøndby Køreskole ved vi, at alle elever lærer forskelligt. Derfor tilbyder vi fleksible kørekortpakker i Brøndby, så du kan få undervisning på den måde, der passer bedst til dig og din hverdag.",
    popular: "Populær",
    fromLabel: "Fra",
    cards: [
      {
        nameKey: "personbil",
        name: "Personbil",
        desc: "Kategori B — lovpakke med teori, kørelektioner og prøver. Dansk, engelsk eller automatgear.",
      },
      {
        nameKey: "motorcykel",
        name: "Motorcykel",
        desc: "A1, A2 og A. Vi har motorcyklerne, den godkendte bane og erfaringen med MC-undervisning.",
      },
      {
        nameKey: "trailer",
        name: "Trailer",
        desc: "B+ og BE kørekort. Perfekt til campingvogn, hestetrailer, bådtrailer eller arbejdskørsel.",
      },
      {
        nameKey: "generhvervelse",
        name: "Generhvervelse",
        desc: "Mistet dit kørekort? Vi hjælper dig diskret gennem kontrollerende køreprøve og ANT-kursus.",
      },
    ],
    disclaimer: "",
  },

  // ─── Learn to Drive (forside) ────────────────────────────────────
  learnToDrive: {
    eyebrow: "Lær at køre bil",
    title: "Tag dit kørekort med",
    titleEm: "tryghed og erfaring",
    lead:
      "Vi tilbyder ubegrænset teoriundervisning i op til 12 måneder – helt uden ekstra omkostninger.",
    stats: [
      { num: "80%", label: "Består teoriprøven første gang" },
      { num: "0%", label: "Utilfredse elever" },
      { num: "100%", label: "Gennemfører deres kørekort" },
    ],
    bullets: [
      { strong: "Erfarne og engagerede kørelærere", suffix: "" },
      { strong: "Dokumenteret høj beståelsesprocent", suffix: "" },
      { strong: "Fleksible tider", suffix: " – også efter skole og arbejde" },
    ],
  },

  // ─── 3 column benefits ───────────────────────────────────────────
  threeColumnBenefits: {
    items: [
      {
        title: "Personlig undervisning",
        desc: "Hos os får du ikke en standardløsning – vi tilpasser undervisningen efter dine behov og din læringsstil.",
      },
      {
        title: "Erfarne kørelærere",
        desc: "Vores rutinerede instruktører kender vejen til et kørekort og guider dig trygt hele vejen.",
      },
      {
        title: "Fleksible tider",
        desc: "Vi tilpasser køretimerne efter din hverdag – uanset om du går i skole, arbejder eller har travlt i fritiden.",
      },
    ],
  },

  // ─── Split Proof Urgent ──────────────────────────────────────────
  splitProofUrgent: {
    proofTitlePart1: "10.000+ elever.",
    proofTitlePart2: "Vi kender",
    proofTitleEm: "Vestegnen",
    proofPercent: "80%",
    proofBody:
      "af vores elever består teoriprøven første gang — dokumenteret i samarbejde med Færdselsstyrelsen.",
    urgentTitle: "Næste",
    urgentTitleEm: "holdstart",
    urgentSub: "Små hold sikrer personlig undervisning",
    urgentEmpty: "Ingen kommende hold lige nu",
    seeAllLink: "Se alle holdstart →",
  },

  // ─── Hvorfor os ──────────────────────────────────────────────────
  whyUs: {
    title: "Hvorfor",
    titleEm: "os",
    lead:
      "Tryg, effektiv og personlig vej til kørekortet — med en køreskole der har kørt i Brøndby siden 2013.",
    items: [
      { strong: "2 min fra Brøndbyøster St.", body: " — kom nemt med S-tog." },
      { strong: "Ubegrænset teori i 12 mdr.", body: " — uden ekstra omkostninger." },
      { strong: "80% består 1. gang", body: " — dokumenteret beståelsesprocent." },
      { strong: "Dansk og engelsk", body: " — også med automatgear." },
    ],
  },

  // ─── Instructors Grid ────────────────────────────────────────────
  instructorsGrid: {
    eyebrow: "Erfarne kørelærere i Brøndby",
    title: "Mød vores",
    titleEm: "dygtige og engagerede",
    titleSuffix: "undervisere",
    lead:
      "Hos Brøndby Køreskole bliver du undervist af erfarne kørelærere, der har hjulpet tusindvis af elever til kørekort. Vi lægger vægt på tryg undervisning, høj faglighed og en positiv oplevelse bag rattet.",
  },

  // ─── Safe and Thorough ───────────────────────────────────────────
  safeAndThorough: {
    eyebrow: "Kvalitet og grundighed",
    title: "Tryg og grundig",
    titleEm: "køreundervisning",
    titleSuffix: "i Brøndby",
    body: [
      "Hos Brøndby Køreskole handler et kørekort ikke kun om at bestå en prøve. Det handler om at blive en tryg og sikker bilist. Vi prioriterer kvalitet og grundighed, og du får undervisning med struktur, ro og klare rammer, så du ved præcis, hvad du skal kunne, og hvorfor det er vigtigt.",
      "Vi tilbyder forløb til personbil, motorcykel og trailer samt generhvervelse.",
      "Vi er ikke nødvendigvis den billigste på pakkeprisen. Til gengæld er målet, at du lærer det rigtigt fra start, og mange oplever derfor, at de har brug for langt færre timer undervejs. Det kan i praksis ofte betyde, at den samlede pris ender med at blive lavere, fordi du slipper for unødige ekstratimer.",
    ],
  },

  // ─── Google Reviews ──────────────────────────────────────────────
  googleReviews: {
    eyebrow: "Anmeldelser",
    title: "Hvad vores",
    titleEm: "elever siger",
    lead:
      "Læs med blandt de mange tilfredse elever, der har taget deres kørekort hos Brøndby Køreskole.",
  },

  // ─── Passed Students Slider ──────────────────────────────────────
  passedStudents: {
    eyebrow: "Tillykke til vores elever",
    title: "Beståede",
    titleEm: "køreprøver",
    lead:
      "Mød nogle af de mange elever, der har taget kørekortet hos Brøndby Køreskole.",
    altText: "Bestået køreprøve hos Brøndby Køreskole",
    altTextMc: "Bestået motorcykel-køreprøve hos Brøndby Køreskole",
    ariaLabel: "Beståede elever",
    ariaCarousel: "karrusel",
  },

  // ─── Location ────────────────────────────────────────────────────
  location: {
    title: "Kom",
    titleEm: "forbi",
    lead:
      "Vi holder til midt i Brøndby — 2 min gang fra Brøndbyøster Station.",
    addressNote: "2 min gang fra Brøndbyøster Station · Man–fre 08–19",
    mapTitle: "Google Maps — Brøndby Køreskole, Nygårds Plads 2K",
  },

  // ─── Contact Form ────────────────────────────────────────────────
  contactForm: {
    successTitle: "Tak for din besked!",
    successBody: "Vi vender tilbage hurtigst muligt — typisk inden for en time.",
    errorDefault: "Kunne ikke sende. Prøv igen.",
  },

  // ─── Subpage hero (breadcrumbs etc.) ─────────────────────────────
  subpage: {
    breadcrumbAriaLabel: "Brødkrumme-sti",
  },

  // ─── /personbil sub-page ─────────────────────────────────────────
  personbil: {
    metaTitle: "Personbil — Kørekort til kategori B",
    metaDescription:
      "Tag dit kørekort til personbil hos Brøndby Køreskole. Lovpakke fra 13.495 kr med teori, kørelektioner og prøver.",
    breadcrumbLabel: "Personbil",
    eyebrow: "Kørekort til bil",
    title: "Tag dit kørekort til",
    titleEm: "personbil",
    titlePunctuation: ".",
    lead:
      "Alt du skal bruge — teori, kørelektioner, glatbane og prøver — samlet i én fast pris. Manuelt eller automatgear.",

    // Section 1: Undervisning på dine præmisser
    teachingTermsEyebrow: "Undervisning på dine præmisser",
    teachingTermsTitle: "Undervisning på dine præmisser —",
    teachingTermsTitleEm: "med fokus på resultater",
    teachingTermsLead:
      "Alle lærer forskelligt. Derfor har vi udviklet en undervisningsmodel, der tilpasser sig den enkelte elev.",
    teachingTermsBullets: [
      {
        text: "Alt undervisningsmateriale er tilgængeligt hjemmefra, så man kan øve når og hvor det passer bedst",
        strong: "når og hvor det passer bedst",
      },
      {
        text: "Online adgang til teoriprøver giver mulighed for løbende at teste niveau og progression",
        strong: "løbende at teste niveau og progression",
      },
      {
        text: "Vores erfarne og pædagogiske kørelærere sikrer, at pensum gennemgås i et roligt og struktureret tempo",
        strong: "roligt og struktureret tempo",
      },
    ],
    teachingTermsClosing:
      "Vi ved, at teori ikke altid er det mest motiverende. Men vi gør det overskueligt, forståeligt – og effektivt. Målet er enkelt: At eleverne består i første forsøg. Og vi skaber rammerne, der gør det muligt.",
    imageAlt: "Skolevogn — Brøndby Køreskole",

    // Section 2: Kvalitet i undervisningen
    qualityTitle: "Kvalitet i undervisningen —",
    qualityTitleEm: "lavere totalomkostning",
    qualityLead: "Vi ved, at vi ikke er de billigste på markedet fra start – og det er et bevidst valg.",
    qualityBody1:
      "Alt for mange elever ender med at betale langt mere end forventet, fordi en billig grundpakke ofte kræver dyre ekstratimer, når undervisningen ikke er tilstrækkelig.",
    qualityBody2: "Hos os får du en anden oplevelse:",
    qualityBullets: [
      { strong: "Vi sætter en høj standard", suffix: " for undervisningens kvalitet" },
      { strong: "Vi anvender dokumenteret, fleksibelt undervisningsmateriale", suffix: "" },
      { strong: "Vi møder den enkelte elev på deres niveau", suffix: " for at sikre størst muligt udbytte" },
    ],
    qualityClosing:
      "Resultatet er, at eleverne hurtigere og mere effektivt bliver klar til prøverne – og dermed reduceres den samlede pris. I praksis betyder det, at vi oftest er den mest omkostningseffektive løsning på den lange bane.",

    // Section 3: Automatgear
    automatTitle: "Kørekort i automatgear —",
    automatTitleEm: "med mulighed for manuelt",
    automatLead:
      "Hos Brøndby Køreskole kan du tage dit kørekort i automatgear – og stadig få lov til at køre bil med manuelt gear. Det afhænger af, hvilken kode der kommer til at stå på dit kørekort.",
    code78Title: "Hvad betyder kode 78?",
    code78Items: [
      "Du har taget den praktiske prøve i en bil med automatgear",
      "Du må derfor kun køre biler med automatgear",
      "Du har ikke dokumenteret evne til at køre manuelt gear",
    ],
    code148Title: "Hvad betyder kode 148?",
    code148Items: [
      { text: "Kode 148 blev indført den 1. juli 2024", strong: null as string | null },
      { text: "Du tog køreprøven i automatgear, men har fået undervisning i manuelt gear", strong: null as string | null },
      {
        text: "Du må køre både automat og manuel bil, selvom prøven blev taget i automatgear",
        strong: "både automat og manuel bil",
      },
    ],
    automatClosing1: "For at få kode 148 skal du gennemføre",
    automatClosing2Strong: "4 lektioner på manøvrebane",
    automatClosing3: "med manuelt gear samt",
    automatClosing4Strong: "3 lektioner i trafikken",
    automatClosing5: "med manuelt gear.",

    // Section 4: Pakke
    packageTitle: "Valg af",
    packageTitleEm: "pakke",
    packageLead:
      "Uanset hvilken lovpakke du vælger hos os, kan du frit vælge, om du vil tage kørekortet i manuel eller automat. Valget er dit – og vi hjælper dig hele vejen.",
    packageBody:
      "Alle er forskellige – og det gælder også måden, vi lærer på. Derfor har vi sammensat forskellige pakker, så du kan vælge det forløb, der passer bedst til dig og dine behov.",
    packageWhyItems: [
      "Uanset om du vælger manuel eller automat, gælder de samme pakker",
      "Du får fleksibel undervisning med fokus på tryghed og kvalitet",
      "Vælg den pakke, der passer til dit tempo og din livsstil",
    ],
    paymentBannerTitle: "Del betalingen over",
    paymentBannerTitleEm: "3 gange",
    paymentBannerSuffix: "— uden merpris!",
    paymentBannerBody:
      "Vi tilbyder afdragsordning i 3 rater — uden renter eller ekstra gebyrer. Spørg os, når du tilmelder dig.",

    // Pakke kort
    pkgLovpakkeName: "Lovpakke",
    pkgLovpakkeDesc: "Det lovpligtige minimum — bestå uden unødvendige tillæg.",
    pkgAllInclusiveName: "All Inclusive Pakke",
    pkgAllInclusiveDesc: "Alt du skal bruge for at bestå første gang — med ro i maven.",
    pkgAllInclusiveBadge: "Mest populær",
    pkgGenerhvervelseName: "Generhvervelses Pakke",
    pkgGenerhvervelseDesc: "Til dig der skal generhverve kørekortet efter frakendelse.",
    pkgGenerhvervelseCta: "Se generhvervelse →",

    pkgCarFeatures: [
      "Ubegrænset teori i 12 mdr.",
      "16 kørelektioner á 45 min.",
      "Manøvrebane 4×45 min",
      "Glatbane 4×45 min",
      "Manuelt eller automatgear",
      "Færdselsrelateret førstehjælp",
      "Online teorikode",
      "Praktisk køreprøve",
    ],
    pkgGenerFeatures: ["Ubegrænset teori", "2 kørelektioner", "Online teorikode"],

    extrasTitle: "Ekstra udgifter",
    extrasTitleEm: "udover pakken",
    extrasRows: [
      { label: "Eksamensgebyr (Færdselsstyrelsen)", value: "1.600 kr" },
      { label: "Trafikrelateret førstehjælp", value: "800 kr" },
      { label: "Online teorikode", value: "400 kr" },
      { label: "Leje af prøvebil", value: "1.400 kr" },
      { label: "Administrations- og genbestillingsgebyr", value: "275 kr" },
      { label: "Ekstra kørelektion (45 min)", value: "fra 550 kr" },
    ],

    // Forløb
    courseImageAlt: "Elever med kørekort — Brøndby Køreskole",
    courseTitle: "Sådan foregår forløbet til",
    courseTitleEm: "kørekort",
    courseLead:
      "Når du tager kørekort til bil (kategori B), får du et forløb med både teori og kørelektioner, så du bliver klar til prøverne med en god plan fra start.",
    courseBody: "Vi hjælper dig med overblik over hele processen, herunder:",
    courseItems: [
      { strong: "Teoriundervisning", body: " med fokus på forståelse, ikke udenadslære" },
      { strong: "Kørelektioner", body: " med progression og tydelig feedback" },
      { strong: "Manøvrebanen", body: " — teknik og grundlæggende kontrol" },
      { strong: "Køreteknisk anlæg", body: " — glatbane, sikkerhed og risikoforståelse" },
      { strong: "Forberedelse til teori- og køreprøve", body: ", så du møder op med ro i maven" },
    ],

    // Færre spildtimer
    spildTitle: "Et kørekortforløb med fokus på",
    spildTitleEm: "færre spildtimer",
    spildLead:
      "Mange oplever, at et godt forløb ikke nødvendigvis handler om at finde den laveste pris. Det handler om at få undervisning, der gør dig klar hurtigere. Når plan og kvalitet hænger sammen, kan du ofte undgå unødvendige ekstratimer og komme i mål på en mere effektiv måde.",

    // Derfor vælger
    whyChooseTitle: "Derfor vælger",
    whyChooseTitleEm: "elever os",
    whyChooseItems: [
      { strong: "Tryg og pædagogisk undervisning", body: " med tydelig struktur" },
      { strong: "Små hold", body: " og god tid til spørgsmål" },
      { strong: "En plan der passer til din hverdag", body: ", så du kan følge med" },
      { strong: "Fokus på at du bliver klar", body: ', ikke bare "færdig"' },
    ],
    whyChooseClosing1: "Klar til at komme i gang?",
    whyChooseClosingContact: "Kontakt os",
    whyChooseClosing2: ", eller se",
    whyChooseClosingClasses: "næste holdstart",
    whyChooseClosing3:
      "— så hjælper vi dig med at finde den rigtige løsning til dit kørekort til bil i Brøndby.",

    // SEO bottom
    seoEyebrow: "Kørekort til bil i Brøndby",
    seoTitle: "Kørekort til bil (kategori B)",
    seoTitleEm: "i Brøndby",
    seoBody1:
      'Vil du tage kørekort til bil et sted, hvor du føler dig tryg hele vejen fra første teoritime til den praktiske prøve? Hos Brøndby Køreskole får du et struktureret og roligt forløb, hvor vi har fokus på kvalitet frem for kvantitet. Det betyder, at du ikke bare "kommer igennem" undervisningen, men bliver reelt klar til at bestå.',
    seoBody2:
      "Vi møder dig i øjenhøjde og tilpasser undervisningen til dit niveau, så du kan udvikle dig i et tempo, der giver mening. Uanset om du er helt ny i trafikken, eller du har brug for ekstra sikkerhed bag rattet, hjælper vi dig sikkert frem mod dit kørekort til personbil.",
  },

  // ─── /motorcykel sub-page (er meget lig — definerer kort her) ────
  motorcykel: {
    metaTitle: "Motorcykel — A1, A2 og A",
    metaDescription:
      "MC-kørekort i Brøndby. A1, A2 og A. Erfarne kørelærere, baneøvelser og køreprøve i eget tempo.",
    breadcrumbLabel: "Motorcykel",
    title: "Mere end bare et",
    titleEm: "motorcykelkørekort",
    titlePunctuation: ".",
    lead:
      "Hos os får du ikke bare et kørekort – du får et skræddersyet forløb, der matcher din alder, erfaring og ambitioner. Uanset om du er nybegynder eller ønsker at opgradere din nuværende kategori, guider vi dig sikkert gennem hele processen.",
  },

  // ─── /trailer sub-page ───────────────────────────────────────────
  trailer: {
    metaTitle: "Trailer (BE)",
    metaDescription:
      "Trailerkørekort BE i Brøndby. Kort forløb, personlig undervisning og prøve inden for få uger.",
    breadcrumbLabel: "Trailer",
    title: "Trailerkørekort",
    titleEm: "(B/E)",
    titlePunctuation: ".",
    lead:
      "Få lov til at køre med de større påhæng. Har du allerede kørekort til kategori B (personbil), kan du udvide det med et B/E-kørekort og dermed lovligt køre med tungere trailer eller påhængsvogn. Med et trailerkørekort får du ret til at føre et vogntog, hvor trailerens tilladte totalvægt er op til 3.500 kg.",
  },

  // ─── /generhvervelse sub-page ────────────────────────────────────
  generhvervelse: {
    metaTitle: "Generhvervelse — efter frakendelse",
    metaDescription:
      "Mistet dit kørekort? Vi hjælper dig diskret gennem kontrollerende køreprøve og ANT-kursus.",
    breadcrumbLabel: "Generhvervelse",
    title: "Generhvervelse af kørekort —",
    titleEm: "når du har fået det frakendt",
    titlePunctuation: ".",
    lead:
      "Har du mistet dit kørekort betinget eller ubetinget? Vi hjælper dig diskret og kompetent gennem kontrollerende køreprøve, ANT-kursus og hele processen.",
  },

  // ─── /priser sub-page ────────────────────────────────────────────
  priser: {
    metaTitle: "Priser — Pakker og tillæg",
    metaDescription:
      "Se priser på kørekortpakker hos Brøndby Køreskole. Personbil, motorcykel, trailer og generhvervelse.",
    breadcrumbLabel: "Priser",
    title: "Transparente priser —",
    titleEm: "vælg den pakke der passer dig",
    titlePunctuation: ".",
    lead:
      'Alle pakker inkluderer ubegrænset teoriundervisning i 12 måneder uden ekstra omkostninger. Priserne er "fra"-priser inkl. officielle afgifter.',
  },

  // ─── /holdstart sub-page ─────────────────────────────────────────
  holdstart: {
    metaTitle: "Holdstart — Næste hold",
    metaDescription: "Se næste hold hos Brøndby Køreskole. Personbil, motorcykel og trailer.",
    breadcrumbLabel: "Holdstart",
    eyebrow: "Holdstart",
    title: "Næste",
    titleEm: "hold",
    lead: "Se næste holdstart i alle kategorier. Klik 'Tilmeld' for at gå videre til booking.",
    emptyMessage: "Ingen kommende hold lige nu — ring 52 40 40 20 for information om næste start.",
    enrollLink: "Tilmeld →",
    categoryLabels: { B: "Bil", A: "Motorcykel", "B/E": "Trailer" },
  },

  // ─── /butik sub-page ─────────────────────────────────────────────
  butik: {
    metaTitle: "Butik",
    metaDescription: "Brøndby Køreskoles butik — kommer snart.",
    breadcrumbLabel: "Butik",
    eyebrow: "Brøndby Køreskole shop",
    title: "Vores",
    titleEm: "butik",
    lead:
      "Vores webshop med teoribøger, øvelseshæfter og merchandise er på vej. Indtil da kan du købe materialer direkte i køreskolen — kig forbi på Nygårds Plads 2K.",
    soonTitle: "Webshop kommer snart",
    soonBody:
      "Vi arbejder på en webshop med teoribøger, øvelseshæfter, t-shirts og andet merchandise. Følg os på sociale medier for opdateringer.",
  },

  // ─── /om-os sub-page ─────────────────────────────────────────────
  omOs: {
    metaTitle: "Om os",
    metaDescription:
      "Mød teamet bag Brøndby Køreskole. Lokal, personlig og tryg køreundervisning siden 2013.",
    breadcrumbLabel: "Om os",
    eyebrow: "Om Brøndby Køreskole",
    title: "Trygt, professionelt og",
    titleEm: "personligt",
    lead:
      "Vi har siden 2013 hjulpet tusindvis af elever i Brøndby og omegn med at tage kørekort. Mød teamet, der er klar til at hjælpe dig.",
    historyTitle: "Vores historie",
    historyBody: [
      "Brøndby Køreskole har siden 2013 været en fast del af Brøndby. Vi tager udgangspunkt i den enkelte elev og tilpasser undervisningen til netop dine behov.",
      "I august 2025 overtog Danny Fernandez ejerskabet af køreskolen — som tidligere set på TV2. Med Danny i spidsen fortsætter vi med samme høje kvalitet, men med et frisk team og ny energi.",
      "Vi tilbyder undervisning på dansk og engelsk, og vores køreskole ligger 2 minutters gang fra Brøndbyøster Station — så du nemt kan komme til både teori og kørelektioner.",
    ],
    faqTitle: "Ofte stillede spørgsmål",
  },

  // ─── /kontakt sub-page ───────────────────────────────────────────
  kontakt: {
    metaTitle: "Kontakt",
    metaDescription:
      "Kontakt Brøndby Køreskole — telefon, e-mail og adresse. Vi svarer typisk inden for en time.",
    breadcrumbLabel: "Kontakt",
    eyebrow: "Kontakt Brøndby Køreskole",
    title: "Vi er klar til at",
    titleEm: "hjælpe",
    lead: "Ring, send sms eller skriv en mail — vi svarer typisk inden for en time.",
    contactCardCallTitle: "Ring til os",
    contactCardEmailTitle: "Skriv til os",
    contactCardHoursTitle: "Åbningstider",
    sendTitle: "Send en besked",
    sendBody: "Vi svarer typisk inden for en time.",
  },

  handelsbetingelser: {
    metaTitle: "Handelsbetingelser",
    metaDescription: "Brøndby Køreskoles handelsbetingelser.",
    breadcrumbLabel: "Handelsbetingelser",
    title: "Handels",
    titleEm: "betingelser",
    lead: "Vores handelsbetingelser for kørekortpakker og tilkøb.",
  },

  privatliv: {
    metaTitle: "Cookie- og privatlivspolitik",
    metaDescription: "Brøndby Køreskoles privatlivspolitik og cookie-information.",
    breadcrumbLabel: "Privatliv",
    title: "Cookie- og",
    titleEm: "privatlivspolitik",
    lead: "Sådan håndterer vi dine data.",
  },
};

const enRaw: typeof daRaw = {
  common: {
    callUs: "Call us",
    smsUs: "Send SMS",
    findCourse: "Find your course →",
    seeAllPrices: "See all prices",
    seePrices: "See prices",
    seeMore: "Read more",
    contactUs: "Contact us",
    enrollClass: "Sign up",
    seeClasses: "See classes",
    nextClass: "Next class",
    seatsAvailable: "Seats available",
    seatsLabel: "Seats",
    fullLabel: "Full",
    fullyBooked: "Fully booked",
    sendMessage: "Send message →",
    name: "Name",
    email: "Email",
    phone: "Phone",
    phoneOptional: "Phone (optional)",
    message: "What's on your mind?",
    sending: "Sending...",
    loading: "Loading…",
    noUpcoming: "No upcoming classes",
    home: "Home",
    openMenu: "Open menu",
    closeMenu: "Close menu",
    quickContact: "Quick contact",
    callShort: "Call",
    next: "Next",
    previous: "Previous",
    free: "From",
    standardPackageFrom: "Standard package from",
    openingHoursWeekdays: "Mon–Fri 08–19",
  },

  nav: {
    bil: "Car",
    motorcykel: "Motorcycle",
    trailer: "Trailer",
    priser: "Prices",
    holdstart: "Classes",
    butik: "Shop",
    omOs: "About",
    kontakt: "Contact",
    artikler: "Articles",
    forside: "Home",
    generhvervelse: "Licence recovery",
    seHold: "See classes",
    primaryAriaLabel: "Primary",
  },

  megaBil: {
    col1Heading: "Car licence",
    col1: [
      { title: "Personal car", desc: "Category B — full package", href: "/personbil" },
      { title: "Automatic", desc: "Without shifting gears", href: "/personbil" },
      { title: "In English", desc: "English driving school", href: "/en/personbil" },
    ],
    col2Heading: "Licence recovery",
    col2: [
      { title: "Licence recovery", desc: "After revocation", href: "/generhvervelse" },
      { title: "Control test", desc: "How to prepare", href: "/generhvervelse" },
      { title: "ANT course", desc: "After alcohol revocation", href: "/generhvervelse" },
    ],
    col3Heading: "Other",
    col3: [
      { title: "See all prices", desc: "Packages and add-ons", href: "/priser" },
      { title: "Next class start", desc: "Available seats", href: "/holdstart" },
      { title: "Find your path", desc: "Guide to choosing", href: "/personbil" },
    ],
    promoKicker: "Popular",
    promoBody: "All inclusive package — everything you need to pass first time.",
    promoCta: "See the package →",
    promoHref: "/personbil",
  },

  header: {
    homeAriaLabel: "Brøndby Køreskole — Home",
  },

  footer: {
    description:
      "Your local driving school in Brøndby since 2013. Car, motorcycle, trailer and licence recovery — with focus on quality and safety.",
    coursesHeader: "Courses",
    schoolHeader: "The school",
    contactHeader: "Contact",
    teachingLocation: "Teaching",
    companyAddressLabel: "Company address",
    companyAddress: "Hvidovre Torv 6A, 2650 Hvidovre",
    termsLink: "Terms",
    privacyLink: "Privacy policy",
    developedBy: "Developed by Firma360",
    copyright: "© {year} Brøndby Køreskole",
    cvr: "CVR {cvr}",
  },

  hero: {
    eyebrow: "Your local driving school in Brøndby",
    title: "Your safe way to a driving licence —",
    titleEm: "with quality and experience",
    titlePunctuation: ".",
    lead: "Serious driving instruction, in a relaxed and motivating environment.",
    statYears: "Years of experience",
    statClients: "Satisfied students",
    statInstructors: "Skilled instructors",
    statRating: "on Facebook",
    callToAction: "Call +45 52 40 40 20 for information about the next class",
  },

  newOwnerStrip: {
    ariaLabel: "New owner and new team",
    tag: "New since Aug 2025",
    headlineStrong: "New owners! New team! New energy!",
    body:
      "Danny Fernandez has taken over Brøndby Køreskole — same location, fresh approach, continued high quality. As previously featured on TV2.",
  },

  callback: {
    eyebrow: "Get a callback",
    title: "Let's have",
    titleEm: "a chat",
    body:
      "Send us an SMS — let us know when it suits you to be called, and we'll get back to you at the requested time.",
    cta: "Call me →",
    smsBody: "Please call me.",
  },

  featureHighlights: {
    items: [
      { title: "Featured on TV2", desc: "Maybe you've already seen us on screen" },
      { title: "High pass rate", desc: "We guide you all the way to your licence" },
      { title: "Flexible lesson times", desc: "Adapted to school, work and free time" },
      { title: "Pedagogical theory lessons", desc: "Focusing on what works in practice" },
    ],
  },

  introText: {
    eyebrow: "Local driving school since 2013",
    title: "Brøndby Køreskole —",
    titleEm: "your local driving school",
    titleSuffix: "in Brøndby",
    lead: "Safe, efficient and professional.",
    body: [
      "Brøndby Køreskole has helped students in Brøndby and the surrounding area get their licence since 2013.",
      "We are located at Nygårds Plads 2K – right next to Brøndbyøster Station, so you can easily reach theory lessons by both train and bus.",
    ],
    goalLabel: "Our goal is clear:",
    goalBody:
      "To give you a safe, thorough and modern path to your driving licence. With us you don't just get competitive prices — first and foremost, you get quality teaching, focused on producing safe and responsible drivers.",
    bullets: [
      { text: "Experienced and engaged instructors who support you all the way", strong: "Experienced and engaged instructors" },
      { text: "A relaxed and motivating learning environment", strong: "A relaxed and motivating" },
      { text: "Theory lessons and driving lessons at your own pace", strong: "Theory lessons and driving lessons" },
      { text: "Options for car, motorcycle, trailer and English classes", strong: "car, motorcycle, trailer" },
    ],
    closingBody:
      "Whether you're a first-time student or you need to recover your licence, we're ready to help. At Brøndby Køreskole you're guaranteed serious teaching, a high pass rate and a great experience all the way.",
  },

  gondrive: {
    tag: "Powered by GonDrive",
    title: "The whole journey — gathered in one place",
    body:
      "We use GonDrive as our student and admin system, so you have booking, theory, schedule and payment all in one place — both on mobile and desktop.",
    chips: ["Booking", "Theory", "Schedule", "Payment"],
  },

  forloeb: {
    eyebrow: "Driving licence packages in Brøndby",
    title: "Find the",
    titleEm: "driving licence package",
    titleSuffix: "that suits you",
    lead:
      "At Brøndby Køreskole we know all students learn differently. Therefore, we offer flexible packages, so you can get instruction that suits you and your everyday life.",
    popular: "Popular",
    fromLabel: "From",
    cards: [
      {
        nameKey: "personbil",
        name: "Car",
        desc: "Category B — standard package with theory, lessons and exams. Danish, English or automatic.",
      },
      {
        nameKey: "motorcykel",
        name: "Motorcycle",
        desc: "A1, A2 and A. We have the motorcycles, the approved track and the MC teaching experience.",
      },
      {
        nameKey: "trailer",
        name: "Trailer",
        desc: "B+ and BE licence. Perfect for caravans, horse trailers, boat trailers or work driving.",
      },
      {
        nameKey: "generhvervelse",
        name: "Licence recovery",
        desc: "Lost your licence? We help you discreetly through the controlling exam and ANT course.",
      },
    ],
    disclaimer: "",
  },

  learnToDrive: {
    eyebrow: "Learn to drive a car",
    title: "Get your licence with",
    titleEm: "safety and experience",
    lead:
      "We offer unlimited theory lessons for up to 12 months — at no extra cost.",
    stats: [
      { num: "80%", label: "Pass theory exam first time" },
      { num: "0%", label: "Unsatisfied students" },
      { num: "100%", label: "Complete their licence" },
    ],
    bullets: [
      { strong: "Experienced and engaged instructors", suffix: "" },
      { strong: "Documented high pass rate", suffix: "" },
      { strong: "Flexible times", suffix: " — also after school and work" },
    ],
  },

  threeColumnBenefits: {
    items: [
      {
        title: "Personal teaching",
        desc: "You don't get a standard solution — we adapt our teaching to your needs and learning style.",
      },
      {
        title: "Experienced instructors",
        desc: "Our seasoned instructors know the road to a licence and guide you safely all the way.",
      },
      {
        title: "Flexible times",
        desc: "We adapt lessons to your day — whether you're at school, working or busy with leisure.",
      },
    ],
  },

  splitProofUrgent: {
    proofTitlePart1: "10,000+ students.",
    proofTitlePart2: "We know",
    proofTitleEm: "Vestegnen",
    proofPercent: "80%",
    proofBody:
      "of our students pass the theory exam first time — documented in collaboration with the Danish Road Safety Authority.",
    urgentTitle: "Next",
    urgentTitleEm: "classes",
    urgentSub: "Small classes ensure personal teaching",
    urgentEmpty: "No upcoming classes right now",
    seeAllLink: "See all classes →",
  },

  whyUs: {
    title: "Why",
    titleEm: "us",
    lead:
      "Safe, efficient and personal way to your licence — with a driving school that has been driving in Brøndby since 2013.",
    items: [
      { strong: "2 min from Brøndbyøster St.", body: " — easy to reach by S-train." },
      { strong: "Unlimited theory for 12 months", body: " — at no extra cost." },
      { strong: "80% pass first time", body: " — documented pass rate." },
      { strong: "Danish and English", body: " — also with automatic gear." },
    ],
  },

  instructorsGrid: {
    eyebrow: "Experienced instructors in Brøndby",
    title: "Meet our",
    titleEm: "skilled and engaged",
    titleSuffix: "instructors",
    lead:
      "At Brøndby Køreskole you're taught by experienced instructors who have helped thousands of students get their licence. We focus on safe instruction, high quality and a positive experience behind the wheel.",
  },

  safeAndThorough: {
    eyebrow: "Quality and thoroughness",
    title: "Safe and thorough",
    titleEm: "driving instruction",
    titleSuffix: "in Brøndby",
    body: [
      "At Brøndby Køreskole, getting a driving licence isn't just about passing an exam. It's about becoming a safe and confident driver. We prioritise quality and thoroughness, and you get teaching with structure, calmness and clear frameworks, so you know exactly what you need to know — and why it matters.",
      "We offer courses for car, motorcycle and trailer as well as licence recovery.",
      "We aren't necessarily the cheapest on package price. Instead, the goal is that you learn it right from the start, and many find they need far fewer lessons. In practice this often means the total cost ends up being lower, because you avoid unnecessary extra hours.",
    ],
  },

  googleReviews: {
    eyebrow: "Reviews",
    title: "What our",
    titleEm: "students say",
    lead:
      "Read along with the many satisfied students who have got their driving licence at Brøndby Køreskole.",
  },

  passedStudents: {
    eyebrow: "Congratulations to our students",
    title: "Passed",
    titleEm: "driving exams",
    lead:
      "Meet some of the many students who have got their driving licence at Brøndby Køreskole.",
    altText: "Passed driving exam at Brøndby Køreskole",
    altTextMc: "Passed motorcycle exam at Brøndby Køreskole",
    ariaLabel: "Passed students",
    ariaCarousel: "carousel",
  },

  location: {
    title: "Visit",
    titleEm: "us",
    lead:
      "We're located in the centre of Brøndby — 2 min walk from Brøndbyøster Station.",
    addressNote: "2 min walk from Brøndbyøster Station · Mon–Fri 08:00–19:00",
    mapTitle: "Google Maps — Brøndby Køreskole, Nygårds Plads 2K",
  },

  contactForm: {
    successTitle: "Thanks for reaching out!",
    successBody: "We'll get back to you as soon as possible — typically within an hour.",
    errorDefault: "Could not send. Please try again.",
  },

  subpage: {
    breadcrumbAriaLabel: "Breadcrumb",
  },

  personbil: {
    metaTitle: "Car — Category B driving licence",
    metaDescription:
      "Get your category B driving licence at Brøndby Køreskole. Standard package from 13,495 DKK.",
    breadcrumbLabel: "Car",
    eyebrow: "Car driving licence",
    title: "Get your",
    titleEm: "car",
    titlePunctuation: " driving licence.",
    lead:
      "Everything you need — theory, lessons, skid pad and exams — all in one fixed price. Manual or automatic transmission.",

    teachingTermsEyebrow: "Teaching on your terms",
    teachingTermsTitle: "Teaching on your terms —",
    teachingTermsTitleEm: "focused on results",
    teachingTermsLead:
      "Everyone learns differently. Therefore, we have developed a teaching model that adapts to the individual student.",
    teachingTermsBullets: [
      {
        text: "All teaching material is available from home, so you can practice whenever and wherever it suits you best",
        strong: "whenever and wherever it suits you best",
      },
      {
        text: "Online access to theory exams gives you the option to continuously test your level and progress",
        strong: "continuously test your level and progress",
      },
      {
        text: "Our experienced and pedagogical instructors ensure that the curriculum is reviewed at a calm and structured pace",
        strong: "a calm and structured pace",
      },
    ],
    teachingTermsClosing:
      "We know theory isn't always the most motivating. But we make it manageable, understandable – and effective. The goal is simple: that students pass first time. And we create the framework that makes it possible.",
    imageAlt: "Driving school car — Brøndby Køreskole",

    qualityTitle: "Quality teaching —",
    qualityTitleEm: "lower total cost",
    qualityLead: "We know we aren't the cheapest on the market from the start – and that's a deliberate choice.",
    qualityBody1:
      "Too many students end up paying far more than expected, because a cheap base package often requires expensive extra lessons when teaching is insufficient.",
    qualityBody2: "With us you get a different experience:",
    qualityBullets: [
      { strong: "We set a high standard", suffix: " for teaching quality" },
      { strong: "We use documented, flexible teaching material", suffix: "" },
      { strong: "We meet the individual student at their level", suffix: " to ensure maximum benefit" },
    ],
    qualityClosing:
      "The result is that students get ready for exams faster and more efficiently — reducing the overall price. In practice, we are usually the most cost-effective solution in the long run.",

    automatTitle: "Automatic transmission licence —",
    automatTitleEm: "with manual option",
    automatLead:
      "At Brøndby Køreskole you can take your licence with automatic transmission – and still be allowed to drive a manual car. It depends on which code is printed on your licence.",
    code78Title: "What does code 78 mean?",
    code78Items: [
      "You took the practical exam in an automatic car",
      "You may therefore only drive automatic cars",
      "You haven't documented the ability to drive manual transmission",
    ],
    code148Title: "What does code 148 mean?",
    code148Items: [
      { text: "Code 148 was introduced on 1 July 2024", strong: null as string | null },
      { text: "You took the exam in an automatic, but were taught with manual transmission", strong: null as string | null },
      {
        text: "You may drive both automatic and manual cars, even though the exam was taken in automatic",
        strong: "both automatic and manual cars",
      },
    ],
    automatClosing1: "To get code 148 you must complete",
    automatClosing2Strong: "4 lessons on manoeuvre track",
    automatClosing3: "with manual transmission and",
    automatClosing4Strong: "3 lessons in traffic",
    automatClosing5: "with manual transmission.",

    packageTitle: "Choosing your",
    packageTitleEm: "package",
    packageLead:
      "No matter which standard package you choose, you can freely choose whether to take your licence with manual or automatic transmission. The choice is yours – and we'll help you all the way.",
    packageBody:
      "Everyone is different – and so is how we learn. Therefore, we have put together different packages so you can choose the course that best suits you and your needs.",
    packageWhyItems: [
      "Whether you choose manual or automatic, the same packages apply",
      "You get flexible teaching focused on safety and quality",
      "Choose the package that fits your pace and lifestyle",
    ],
    paymentBannerTitle: "Split payment over",
    paymentBannerTitleEm: "3 instalments",
    paymentBannerSuffix: "— at no extra cost!",
    paymentBannerBody:
      "We offer 3-instalment payment plans — without interest or extra fees. Ask us when you sign up.",

    pkgLovpakkeName: "Standard package",
    pkgLovpakkeDesc: "The legal minimum — pass without unnecessary extras.",
    pkgAllInclusiveName: "All Inclusive Package",
    pkgAllInclusiveDesc: "Everything you need to pass first time — peace of mind included.",
    pkgAllInclusiveBadge: "Most popular",
    pkgGenerhvervelseName: "Licence recovery",
    pkgGenerhvervelseDesc: "For you who need to recover your licence after revocation.",
    pkgGenerhvervelseCta: "See licence recovery →",

    pkgCarFeatures: [
      "Unlimited theory for 12 months",
      "16 lessons of 45 min",
      "Manoeuvre track 4×45 min",
      "Skid pad 4×45 min",
      "Manual or automatic transmission",
      "Traffic-related first aid",
      "Online theory code",
      "Practical driving exam",
    ],
    pkgGenerFeatures: ["Unlimited theory", "2 driving lessons", "Online theory code"],

    extrasTitle: "Extra fees",
    extrasTitleEm: "on top of the package",
    extrasRows: [
      { label: "Exam fee (Danish Road Safety Authority)", value: "1,600 DKK" },
      { label: "Traffic-related first aid course", value: "800 DKK" },
      { label: "Online theory code", value: "400 DKK" },
      { label: "Exam car rental", value: "1,400 DKK" },
      { label: "Administration & re-booking fee", value: "275 DKK" },
      { label: "Extra driving lesson (45 min)", value: "from 550 DKK" },
    ],

    courseImageAlt: "Students with driving licence — Brøndby Køreskole",
    courseTitle: "How the",
    courseTitleEm: "licence",
    courseLead:
      "When you take your car licence (category B), you get a course with both theory and driving lessons, so you're ready for the exams with a good plan from the start.",
    courseBody: "We help you get an overview of the entire process, including:",
    courseItems: [
      { strong: "Theory teaching", body: " focused on understanding, not memorisation" },
      { strong: "Driving lessons", body: " with progression and clear feedback" },
      { strong: "Manoeuvre track", body: " — technique and basic control" },
      { strong: "Driving-tech facility", body: " — skid pad, safety and risk understanding" },
      { strong: "Theory and exam preparation", body: ", so you arrive with peace of mind" },
    ],

    spildTitle: "A driving course focused on",
    spildTitleEm: "fewer wasted hours",
    spildLead:
      "Many find that a good course isn't necessarily about finding the lowest price. It's about getting instruction that gets you ready faster. When plan and quality go hand in hand, you can often avoid unnecessary extra hours and reach the goal more efficiently.",

    whyChooseTitle: "Why",
    whyChooseTitleEm: "students choose us",
    whyChooseItems: [
      { strong: "Safe and pedagogical instruction", body: " with clear structure" },
      { strong: "Small classes", body: " with time for questions" },
      { strong: "A plan that fits your everyday life", body: ", so you can keep up" },
      { strong: "Focus on you being ready", body: ', not just "done"' },
    ],
    whyChooseClosing1: "Ready to get started?",
    whyChooseClosingContact: "Contact us",
    whyChooseClosing2: ", or see",
    whyChooseClosingClasses: "next class",
    whyChooseClosing3:
      "— and we'll help you find the right solution for your car driving licence in Brøndby.",

    seoEyebrow: "Car licence in Brøndby",
    seoTitle: "Car licence (category B)",
    seoTitleEm: "in Brøndby",
    seoBody1:
      'Want to take your car licence somewhere you feel safe all the way from the first theory lesson to the practical exam? At Brøndby Køreskole you get a structured and calm course where we focus on quality over quantity. This means you don\'t just "get through" the teaching, but truly become ready to pass.',
    seoBody2:
      "We meet you at eye level and adapt the teaching to your level, so you can develop at a pace that makes sense. Whether you're new to traffic, or you need extra security behind the wheel, we'll safely guide you towards your car licence.",
  },

  motorcykel: {
    metaTitle: "Motorcycle — A1, A2 and A",
    metaDescription:
      "MC licence in Brøndby. A1, A2 and A. Experienced instructors, track exercises and exam at your own pace.",
    breadcrumbLabel: "Motorcycle",
    title: "More than just a",
    titleEm: "motorcycle licence",
    titlePunctuation: ".",
    lead:
      "With us you don't just get a licence – you get a tailored course that matches your age, experience and ambitions. Whether you're a beginner or want to upgrade, we'll guide you safely through the entire process.",
  },

  trailer: {
    metaTitle: "Trailer (BE)",
    metaDescription:
      "Trailer BE licence in Brøndby. Short course, personal instruction and exam within a few weeks.",
    breadcrumbLabel: "Trailer",
    title: "Trailer licence",
    titleEm: "(B/E)",
    titlePunctuation: ".",
    lead:
      "Get permission to tow larger trailers. If you already hold a category B licence (car), you can extend it with a B/E licence to legally tow a heavier trailer or caravan. With a trailer licence, you can drive a vehicle combination with a trailer up to 3,500 kg.",
  },

  generhvervelse: {
    metaTitle: "Licence recovery — after revocation",
    metaDescription:
      "Lost your licence? We help you discreetly through the controlling exam and ANT course.",
    breadcrumbLabel: "Licence recovery",
    title: "Licence recovery —",
    titleEm: "when you've had it revoked",
    titlePunctuation: ".",
    lead:
      "Have you lost your licence conditionally or unconditionally? We help you discreetly and competently through the controlling driving exam, ANT course and the entire process.",
  },

  priser: {
    metaTitle: "Prices — Packages & fees",
    metaDescription: "See prices for driving licence packages at Brøndby Køreskole.",
    breadcrumbLabel: "Prices",
    title: "Transparent prices —",
    titleEm: "choose the package that suits you",
    titlePunctuation: ".",
    lead:
      'All packages include unlimited theory instruction for 12 months at no extra cost. Prices are "from" prices including official fees.',
  },

  holdstart: {
    metaTitle: "Classes — Next start",
    metaDescription:
      "See upcoming classes at Brøndby Køreskole. Car, motorcycle and trailer.",
    breadcrumbLabel: "Classes",
    eyebrow: "Classes",
    title: "Next",
    titleEm: "classes",
    lead: "See upcoming classes in all categories. Click 'Sign up' to proceed to booking.",
    emptyMessage:
      "No upcoming classes right now — call +45 52 40 40 20 for information about the next start.",
    enrollLink: "Sign up →",
    categoryLabels: { B: "Car", A: "Motorcycle", "B/E": "Trailer" },
  },

  butik: {
    metaTitle: "Shop",
    metaDescription: "Brøndby Køreskole's shop — coming soon.",
    breadcrumbLabel: "Shop",
    eyebrow: "Brøndby Køreskole shop",
    title: "Our",
    titleEm: "shop",
    lead:
      "Our webshop with theory books, exercise booklets and merchandise is on the way. In the meantime, you can buy materials directly at the school — visit us at Nygårds Plads 2K.",
    soonTitle: "Webshop coming soon",
    soonBody:
      "We're working on a webshop with theory books, exercise booklets, t-shirts and other merchandise. Follow us on social media for updates.",
  },

  omOs: {
    metaTitle: "About",
    metaDescription:
      "Meet the team behind Brøndby Køreskole. Local, personal and safe driving instruction since 2013.",
    breadcrumbLabel: "About",
    eyebrow: "About Brøndby Køreskole",
    title: "Safe, professional and",
    titleEm: "personal",
    lead:
      "Since 2013 we've helped thousands of students in Brøndby and the surrounding area get their driving licence. Meet the team that's ready to help you.",
    historyTitle: "Our story",
    historyBody: [
      "Brøndby Køreskole has been a fixture in Brøndby since 2013. We start with the individual student and adapt our teaching to your specific needs.",
      "In August 2025, Danny Fernandez took over the school — as previously seen on TV2. With Danny leading, we continue with the same high quality but with a fresh team and new energy.",
      "We offer instruction in Danish and English, and our school is a 2-minute walk from Brøndbyøster Station — making it easy to get to both theory and driving lessons.",
    ],
    faqTitle: "Frequently asked questions",
  },

  kontakt: {
    metaTitle: "Contact",
    metaDescription:
      "Contact Brøndby Køreskole — phone, email and address. We typically reply within an hour.",
    breadcrumbLabel: "Contact",
    eyebrow: "Contact Brøndby Køreskole",
    title: "We're ready to",
    titleEm: "help",
    lead: "Call, send an SMS, or email us — we typically reply within an hour.",
    contactCardCallTitle: "Call us",
    contactCardEmailTitle: "Email us",
    contactCardHoursTitle: "Hours",
    sendTitle: "Send a message",
    sendBody: "We typically reply within an hour.",
  },

  handelsbetingelser: {
    metaTitle: "Terms and conditions",
    metaDescription: "Brøndby Køreskole's terms and conditions.",
    breadcrumbLabel: "Terms",
    title: "Terms and",
    titleEm: "conditions",
    lead: "Our terms and conditions for driving licence packages and add-ons.",
  },

  privatliv: {
    metaTitle: "Cookie & privacy policy",
    metaDescription: "Brøndby Køreskole's privacy policy and cookie information.",
    breadcrumbLabel: "Privacy",
    title: "Cookie &",
    titleEm: "privacy policy",
    lead: "How we handle your data.",
  },
};

const da: Dictionary = daRaw;
const en: Dictionary = enRaw;

export function getDictionary(locale: Locale): Dictionary {
  return locale === "en" ? en : da;
}
